/**
 * Fault injection protection for quantum cryptography
 * Prevents attacks that introduce computational errors
 */

import { sha256 } from '@noble/hashes/sha256'
import { constantTimeEqual } from './constant-time'

export interface FaultProtectionConfig {
  enableRedundancy: boolean
  redundancyLevel: number // Number of times to repeat computation
  enableChecksum: boolean
  enableVerification: boolean
}

const defaultConfig: FaultProtectionConfig = {
  enableRedundancy: true,
  redundancyLevel: 3,
  enableChecksum: true,
  enableVerification: true
}

/**
 * Fault detection wrapper for critical operations
 */
export class FaultProtection {
  private config: FaultProtectionConfig
  
  constructor(config: Partial<FaultProtectionConfig> = {}) {
    this.config = { ...defaultConfig, ...config }
  }
  
  /**
   * Execute operation with fault detection
   * Repeats computation and verifies consistency
   */
  async protectedExecute<T>(
    operation: () => Promise<T>,
    validator?: (result: T) => boolean
  ): Promise<T> {
    const results: T[] = []
    
    // Execute operation multiple times
    for (let i = 0; i < this.config.redundancyLevel; i++) {
      try {
        const result = await operation()
        
        // Validate result if validator provided
        if (validator && !validator(result)) {
          throw new Error(`Validation failed for execution ${i}`)
        }
        
        results.push(result)
      } catch (error) {
        console.error(`Fault detected in execution ${i}:`, error)
        if (results.length === 0 && i === this.config.redundancyLevel - 1) {
          throw error
        }
      }
    }
    
    // Verify all results are consistent
    if (results.length > 1) {
      const reference = results[0]
      for (let i = 1; i < results.length; i++) {
        if (!this.compareResults(reference, results[i])) {
          throw new Error('Fault detected: Inconsistent results')
        }
      }
    }
    
    return results[0]
  }
  
  /**
   * Compare two results for consistency
   */
  private compareResults<T>(a: T, b: T): boolean {
    if (a instanceof Uint8Array && b instanceof Uint8Array) {
      return constantTimeEqual(a, b)
    }
    
    if (typeof a === 'object' && typeof b === 'object') {
      return JSON.stringify(a) === JSON.stringify(b)
    }
    
    return a === b
  }
  
  /**
   * Add checksum to data for integrity verification
   */
  static addChecksum(data: Uint8Array): Uint8Array {
    const checksum = sha256(data).slice(0, 4) // 4-byte checksum
    const result = new Uint8Array(data.length + 4)
    result.set(data, 0)
    result.set(checksum, data.length)
    return result
  }
  
  /**
   * Verify and remove checksum from data
   */
  static verifyChecksum(dataWithChecksum: Uint8Array): Uint8Array {
    if (dataWithChecksum.length < 4) {
      throw new Error('Data too short to contain checksum')
    }
    
    const data = dataWithChecksum.slice(0, -4)
    const providedChecksum = dataWithChecksum.slice(-4)
    const calculatedChecksum = sha256(data).slice(0, 4)
    
    if (!constantTimeEqual(providedChecksum, calculatedChecksum)) {
      throw new Error('Checksum verification failed')
    }
    
    return data
  }
}

/**
 * Quantum signature with fault protection
 */
export class FaultProtectedQuantumSignature {
  private faultProtection: FaultProtection
  
  constructor(config?: Partial<FaultProtectionConfig>) {
    this.faultProtection = new FaultProtection(config)
  }
  
  /**
   * Sign with fault protection
   */
  async sign(
    signFunction: (message: Uint8Array) => Promise<Uint8Array>,
    message: Uint8Array,
    privateKey?: Uint8Array
  ): Promise<Uint8Array> {
    // Add message checksum
    const messageWithChecksum = FaultProtection.addChecksum(message)
    
    // Sign with redundancy
    const signature = await this.faultProtection.protectedExecute(
      async () => {
        const sig = await signFunction(messageWithChecksum)
        
        // Add signature checksum
        return FaultProtection.addChecksum(sig)
      },
      (sig) => sig.length > 0
    )
    
    return signature
  }
  
  /**
   * Verify with fault protection
   */
  async verify(
    verifyFunction: (message: Uint8Array, signature: Uint8Array) => Promise<boolean>,
    message: Uint8Array,
    signature: Uint8Array,
    publicKey?: Uint8Array
  ): Promise<boolean> {
    try {
      // Verify signature checksum
      const sigWithoutChecksum = FaultProtection.verifyChecksum(signature)
      
      // Add message checksum
      const messageWithChecksum = FaultProtection.addChecksum(message)
      
      // Verify with redundancy
      return await this.faultProtection.protectedExecute(
        async () => verifyFunction(messageWithChecksum, sigWithoutChecksum),
        (result) => typeof result === 'boolean'
      )
    } catch (error) {
      console.error('Verification failed:', error)
      return false
    }
  }
}

/**
 * Memory corruption detection
 */
export class MemoryGuard {
  private canaries: Map<string, Uint8Array> = new Map()
  
  /**
   * Place canary values around sensitive data
   */
  placeCanary(id: string, size: number = 32): Uint8Array {
    const canary = new Uint8Array(size)
    crypto.getRandomValues(canary)
    this.canaries.set(id, canary)
    return canary
  }
  
  /**
   * Check if canary is intact
   */
  checkCanary(id: string, canary: Uint8Array): boolean {
    const original = this.canaries.get(id)
    if (!original) {
      throw new Error(`Canary ${id} not found`)
    }
    
    return constantTimeEqual(original, canary)
  }
  
  /**
   * Protected data wrapper with canaries
   */
  protectData(data: Uint8Array, id: string): Uint8Array {
    const canarySize = 16
    const canaryBefore = this.placeCanary(`${id}_before`, canarySize)
    const canaryAfter = this.placeCanary(`${id}_after`, canarySize)
    
    const protectedData = new Uint8Array(canarySize + data.length + canarySize)
    protectedData.set(canaryBefore, 0)
    protectedData.set(data, canarySize)
    protectedData.set(canaryAfter, canarySize + data.length)
    
    return protectedData
  }
  
  /**
   * Extract data and verify canaries
   */
  unprotectData(protectedData: Uint8Array, id: string): Uint8Array {
    const canarySize = 16
    
    if (protectedData.length < canarySize * 2) {
      throw new Error('Protected data too short')
    }
    
    const canaryBefore = protectedData.slice(0, canarySize)
    const data = protectedData.slice(canarySize, -canarySize)
    const canaryAfter = protectedData.slice(-canarySize)
    
    if (!this.checkCanary(`${id}_before`, canaryBefore)) {
      throw new Error('Memory corruption detected: before canary')
    }
    
    if (!this.checkCanary(`${id}_after`, canaryAfter)) {
      throw new Error('Memory corruption detected: after canary')
    }
    
    return data
  }
}

/**
 * Computation verification using inverse operations
 */
export class ComputationVerifier {
  /**
   * Verify scalar multiplication by checking inverse
   */
  static verifyScalarMultiplication(
    scalar: bigint,
    point: { x: bigint, y: bigint },
    result: { x: bigint, y: bigint },
    curveOrder: bigint
  ): boolean {
    // Compute scalar inverse
    const scalarInv = modInverse(scalar, curveOrder)
    
    // Multiply result by inverse (should give original point)
    // This is simplified - actual implementation would use proper curve operations
    const checkX = (result.x * scalarInv) % curveOrder
    const checkY = (result.y * scalarInv) % curveOrder
    
    return checkX === point.x && checkY === point.y
  }
  
  /**
   * Verify modular exponentiation
   */
  static verifyModExp(base: bigint, exp: bigint, mod: bigint, result: bigint): boolean {
    // Verify using Fermat's little theorem
    const check = modExp(result, modInverse(exp, mod - 1n), mod)
    return check === base % mod
  }
}

/**
 * Helper functions
 */
function modInverse(a: bigint, m: bigint): bigint {
  let [old_r, r] = [a, m]
  let [old_s, s] = [1n, 0n]
  
  while (r !== 0n) {
    const quotient = old_r / r
    ;[old_r, r] = [r, old_r - quotient * r]
    ;[old_s, s] = [s, old_s - quotient * s]
  }
  
  return old_s < 0n ? old_s + m : old_s
}

function modExp(base: bigint, exp: bigint, mod: bigint): bigint {
  let result = 1n
  base = base % mod
  
  while (exp > 0n) {
    if (exp % 2n === 1n) {
      result = (result * base) % mod
    }
    exp = exp >> 1n
    base = (base * base) % mod
  }
  
  return result
}

export default {
  FaultProtection,
  FaultProtectedQuantumSignature,
  MemoryGuard,
  ComputationVerifier
}